import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import "../styles/Cart.css";

export default function CartPage() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);

  const loadCart = () => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    setCartItems(cart);
  };

  useEffect(() => {
    loadCart();
    const onCartUpdated = () => loadCart();
    window.addEventListener("cartUpdated", onCartUpdated);
    window.addEventListener("storage", (e) => {
      if (e.key === "cart") loadCart();
    });
    return () => window.removeEventListener("cartUpdated", onCartUpdated);
  }, []);

  const updateLocalCart = (updated) => {
    setCartItems(updated);
    localStorage.setItem("cart", JSON.stringify(updated));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const removeItem = (id, type, plan) => {
    const updated = cartItems.filter(
      (item) => !(item.id === id && item.type === type && item.plan === plan),
    );

    updateLocalCart(updated);
  };

  const changeQuantity = (id, delta) => {
    const updated = cartItems
      .map((item) => {
        if (item.id === id) {
          const currentQty = item.quantity || 1;
          const newQty = currentQty + delta;

          // Use 'availableStock' or 'stock' depending on what your SubscriptionPage saves
          const maxStock = Number(item.availableStock || item.stock || 0);

          // If user tries to increase (+) but it exceeds stock
          if (delta > 0 && newQty > maxStock) {
            alert(`Only ${maxStock} items available in stock!`);
            return item;
          }

          return { ...item, quantity: Math.max(1, newQty) };
        }
        return item;
      })
      .filter((item) => item.quantity !== 0);

    updateLocalCart(updated);
  };

  // const emptyCart = () => updateLocalCart([]);

  const subTotal = cartItems.reduce(
    (acc, item) => acc + Number(item.price) * (item.quantity || 1),
    0,
  );
  const totalGST = cartItems.reduce((acc, item) => {
    const taxPercent = Number(item.tax || 0); // from localStorage
    const itemTotal = Number(item.price) * (item.quantity || 1);
    return acc + (itemTotal * taxPercent) / 100;
  }, 0);

  const CGST = +(totalGST / 2).toFixed(2);
  const SGST = +(totalGST / 2).toFixed(2);

  const total = subTotal + CGST + SGST;

  return (
    <div className="container-fluid mt-4 mb-5 page-container">
      <h2 className="mb-4 text-center">🛒 Shopping Cart</h2>

      {cartItems.length === 0 ? (
        <div className="text-center py-5">
          <h5 className="text-muted mb-3">Your cart is empty</h5>
          <Link
            to="/"
            onClick={() => window.scrollTo(0, 0)}
            className="btn btn-primary"
          >
            Continue Shopping
          </Link>
        </div>
      ) : (
        <div className="row summarybox">
          {/* Cart Items Table */}
          <div className="col-12">
            <div className="table-responsive">
              <table className="table table-bordered border-dark-subtle align-middle text-center">
                <thead className="table-light border-dark-subtle">
                  <tr>
                    <th>Image</th>
                    <th>Product Code</th>
                    <th>Product Name</th>
                    <th>Product Plan</th>
                    <th>Product Type</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {cartItems.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <img
                          src={item.image}
                          alt={item.title}
                          style={{
                            width: "80px",
                            height: "100px",
                            objectFit: "cover",
                          }}
                          className="rounded"
                        />
                      </td>
                      <td>{item.code}</td>
                      <td className="text-start">{item.title}</td>
                      <td>{item.plan}</td>
                      <td>{item.type}</td>
                      <td>
                        {/* <div className="d-flex justify-content-center align-items-center gap-2">
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => changeQuantity(item.id, -1)}
                            disabled={
                              item.type === "Subscription" ||
                              (item.type === "Hardcopy" &&
                                item.plan?.toLowerCase() === "yearly")
                            }
                          >
                            -
                          </button>
                          <span>{item.quantity || 1}</span>
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => changeQuantity(item.id, 1)}
                            disabled={
                              item.type === "Subscription" ||
                              (item.type === "Hardcopy" &&
                                item.plan?.toLowerCase() === "yearly") || (item.quantity >= Number(item.stock))
                            } // disable for magazine
                          >
                            +
                          </button>
                        </div> */}
                        <div className="d-flex justify-content-center align-items-center gap-2">
                          {/* MINUS BUTTON */}
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => changeQuantity(item.id, -1)}
                            disabled={
                              item.type === "Subscription" ||
                              (item.type === "Hardcopy" &&
                                item.plan?.toLowerCase() === "yearly") ||
                              item.quantity <= 1 // Already at minimum
                            }
                          >
                            -
                          </button>

                          <span>{item.quantity || 1}</span>

                          {/* PLUS BUTTON */}
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => changeQuantity(item.id, 1)}
                            disabled={
                              item.type === "Subscription" ||
                              (item.type === "Hardcopy" &&
                                item.plan?.toLowerCase() === "yearly") ||
                              item.quantity >=
                                Number(item.availableStock || item.stock || 0) // ✅ Disable if at max stock
                            }
                          >
                            +
                          </button>
                        </div>
                      </td>

                      <td>₹{Number(item.price).toFixed(2)}</td>
                      <td>
                        ₹{Number(item.price * (item.quantity || 1)).toFixed(2)}
                      </td>
                      <td>
                        <button
                          className="btn btn-outline-danger btn-sm gap-1"
                          onClick={() =>
                            removeItem(item.id, item.type, item.plan)
                          }
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* <button
                className="btn btn-outline-danger mt-3"
                onClick={emptyCart}
              >
                Empty Cart
              </button> */}
          </div>
          {/* Totals Box */}
          <div className=" col-10 col-xl-3 col-lg-4 col-md-4 mt-4 mt-lg-0">
            <div className="border p-4 rounded shadow-sm bg-light">
              <h5 className="fw-bold mb-3">Order Summary</h5>
              <div className="d-flex justify-content-between mb-2">
                <span>Sub-Total</span>
                <span>₹{subTotal.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>CGST ({((CGST / subTotal) * 100).toFixed(2)}%)</span>
                <span>₹{CGST.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>SGST ({((SGST / subTotal) * 100).toFixed(2)}%)</span>
                <span>₹{SGST.toFixed(2)}</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between fw-bold fs-5">
                <span>Total</span>
                <span>₹{total.toFixed(2)}</span>
              </div>
              {/* <button
                  className="btn btn-primary w-100 mt-3"
                  onClick={() => navigate("/checkout")}
                >
                  Proceed to Checkout
                </button> */}
            </div>
          </div>
          <div className="d-flex justify-content-between">
            <button
              className="btn btn-success mt-3"
              onClick={() => {
                window.scrollTo(0, 0);
                navigate("/");
              }}
            >
              Continue To Shopping
            </button>
            <button
              className="btn btn-warning mt-3 fw-semibold"
              onClick={() => {
                window.scrollTo(0, 0);
                navigate("/checkout");
              }}
            >
              CheckOut
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
